import type { ReportTableCell, ReportTableProps } from '@/components';
import type { ReportLocalGovernmentFundsTableFundOwnerForLocalGovernmentModel } from '@/features/mold/models';
import { executionProgressFormatAmount } from '@/features/project/projects/detail/execution-progress/_utils/execution-progress';
import { t } from '@/i18n';
import {
  FALLBACK_TEXT,
  type FormatValueByUnitOptions,
  convertToNegative,
  formatNumberForPdf,
  validateTotalWithElements,
} from '@/utils';
import type { CSSProperties } from 'react';
import type { z } from 'zod';
import { centerTextStyle } from '../pdf/pdf-utils';

const rowTextBaseStyle: CSSProperties = {
  textAlign: 'center',
};

const amountTextStyle: CSSProperties = {
  textAlign: 'right',
};

const textLeftStyle: CSSProperties = {
  textAlign: 'left',
};

const AMOUNT_CELL_RATIO = 45;
const AMOUNT_CELL_WIDTH = `${AMOUNT_CELL_RATIO}px` as const;

const getCountAndAmountText = ({
  count,
  amount,
  negativeCount,
  isDebugMode,
}: {
  count: number | undefined | null;
  amount: number | undefined | null;
  negativeCount?: number | null;
  isDebugMode?: boolean;
}) => {
  return `(${formatNumberForPdf(count)})\n${getAmountText(amount, {
    isDebugMode,
    negativeCount,
  })}`;
};

const getAmountText = (
  amount: number | undefined | null,
  option?: Exclude<FormatValueByUnitOptions, 'isDebugMode' | 'negativeCount'>,
) => {
  /* 元々生データが返ってきているのでformatAmountForPdfでraw判定入らない。
  そのため、executionProgressFormatAmountで0/nullの対応を行う
  */
  return executionProgressFormatAmount({
    value: amount,
    negativeValueCount: option?.negativeCount,
    isDebugMode: option?.isDebugMode ?? false,
  });
};

const columnRatios = {
  // 同じレベルは合計で100になるようにする
  fundOwnerForLocalGovernmentIndex: 4,
  fundOwnerForLocalGovernment: 30,
  fundName: 30,
  fundOverviewForLocalGovernment: 36,
  amountOfNationalTreasury: AMOUNT_CELL_WIDTH,
  incomeAndExpenditure: {
    income: [AMOUNT_CELL_WIDTH, `${AMOUNT_CELL_RATIO * 5}px`],
    subsetNationalExpense: [AMOUNT_CELL_WIDTH, `${AMOUNT_CELL_RATIO * 4}px`],
    others: [`${AMOUNT_CELL_RATIO * 3}px`, AMOUNT_CELL_WIDTH],
  },
  endingOfTheFundBalance: Array(2).fill(AMOUNT_CELL_WIDTH),
  decisionOnProjectImplementation: Array(5).fill(AMOUNT_CELL_WIDTH),
  loanBalance: Array(3).fill(AMOUNT_CELL_WIDTH),
};

const getFundOwnerForLocalGovernmentHeader = (
  fiscalYear: number,
): ReportTableProps['header'] => {
  const previousFiscalYear = fiscalYear && fiscalYear - 1;
  // ratioは全体で100になるようにする
  return [
    {
      value: t('report-fund-owner-for-local-government-index'),
      textStyle: rowTextBaseStyle,
      ratio: columnRatios.fundOwnerForLocalGovernmentIndex,
    },
    {
      value: t('fund-owner-for-local-government'),
      textStyle: rowTextBaseStyle,
      ratio: columnRatios.fundOwnerForLocalGovernment,
    },
    {
      value: t('fund-name'),
      textStyle: rowTextBaseStyle,
      ratio: columnRatios.fundName,
    },
    {
      value: t('fund-overview-for-local-government'),
      textStyle: rowTextBaseStyle,
      ratio: columnRatios.fundOverviewForLocalGovernment,
    },
    {
      type: 'multiple',
      ratio: 'auto',
      cell: {
        type: 'subset',
        cellRowRatios: ['1em', 1],
        cellColRatios: [AMOUNT_CELL_WIDTH, AMOUNT_CELL_WIDTH],
        containedCell: {
          value: t(
            'report-local-government-fund-header-beginning-of-the-fund-balance',
            {
              year: previousFiscalYear - 1,
            },
          ),
          // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
          textWrapperStyle: { height: '100%' },
          textStyle: rowTextBaseStyle,
        },
        includedCell: {
          value: t(
            'report-standard-fund-private-header-national-equivalent-of-the-balance',
          ),
          // // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
          textWrapperStyle: { height: '100%' },
          textStyle: rowTextBaseStyle,
        },
      },
    },
    {
      type: 'multiple',
      ratio: 'auto',
      cell: {
        type: 'flex',
        vertical: true,
        cellRatios: ['1em', 1],
        flexCells: [
          {
            value: t(
              'report-local-government-fund-header-income-and-expenditure',
              { year: previousFiscalYear },
            ),
            textStyle: rowTextBaseStyle,
          },
          {
            type: 'multiple',
            cell: {
              type: 'flex',
              flexCells: [
                {
                  type: 'multiple',
                  cell: {
                    type: 'subset',
                    cellRowRatios: ['1em', 1],
                    isContainedCellFullHeight: true,
                    cellColRatios: columnRatios.incomeAndExpenditure.income,
                    containedCell: {
                      type: 'text',
                      // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
                      textWrapperStyle: { height: '100%' },
                      value: t('report-local-government-fund-header-income'),
                      textStyle: rowTextBaseStyle,
                      wrapperStyle: {
                        width: AMOUNT_CELL_WIDTH,
                      },
                    },
                    includedCell: {
                      type: 'multiple',
                      cell: {
                        type: 'subset',
                        cellRowRatios: ['1em', 1],
                        isContainedCellFullHeight: true,
                        cellColRatios:
                          columnRatios.incomeAndExpenditure
                            .subsetNationalExpense,
                        containedCell: {
                          type: 'text',
                          // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
                          textWrapperStyle: { height: '100%' },
                          value: t(
                            'report-local-government-fund-header-amount-equivalent-to-national-expense',
                          ),
                          textStyle: rowTextBaseStyle,
                        },
                        includedCell: {
                          type: 'multiple',
                          cell: {
                            type: 'flex',
                            cellRatios:
                              columnRatios.incomeAndExpenditure.others,
                            flexCells: [
                              {
                                type: 'multiple',
                                cell: {
                                  vertical: true,
                                  flexCells: [
                                    {
                                      ...centerTextStyle,
                                      value: t(
                                        'amount-of-funding-from-the-government',
                                      ),
                                      textStyle: rowTextBaseStyle,
                                    },
                                    {
                                      type: 'multiple',
                                      cell: {
                                        cellRatios:
                                          Array(3).fill(AMOUNT_CELL_WIDTH),
                                        flexCells: [
                                          {
                                            value: t('initial'),
                                            textStyle: rowTextBaseStyle,
                                            ...centerTextStyle,
                                          },
                                          {
                                            value: t('correction'),
                                            textStyle: rowTextBaseStyle,
                                            ...centerTextStyle,
                                          },
                                          {
                                            value: t('reserve-budget'),
                                            textStyle: rowTextBaseStyle,
                                            ...centerTextStyle,
                                          },
                                        ],
                                      },
                                    },
                                  ],
                                },
                              },
                              {
                                value: t('others'),
                                ...centerTextStyle,
                                textStyle: rowTextBaseStyle,
                              },
                            ],
                          },
                        },
                      },
                    },
                  },
                },
                {
                  align: 'center',
                  value: t('report-local-government-fund-header-expense'),
                  textStyle: rowTextBaseStyle,
                  wrapperStyle: {
                    width: AMOUNT_CELL_WIDTH,
                    display: 'flex',
                  },
                  textWrapperStyle: { width: '100%' },
                },
              ],
            },
          },
        ],
      },
    },
    {
      ratio: 'auto',
      value: t(
        'report-local-government-fund-header-amount-of-national-treasury-return',
        { year: previousFiscalYear },
      ),
      textWrapperStyle: { width: AMOUNT_CELL_WIDTH },
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'multiple',
      ratio: 'auto',
      cell: {
        type: 'subset',
        cellColRatios: columnRatios.endingOfTheFundBalance,
        containedCell: {
          type: 'text',
          // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
          textWrapperStyle: { height: '100%' },
          value: t(
            'report-local-government-fund-header-ending-of-the-fund-balance',
            {
              year: previousFiscalYear,
            },
          ),
          textStyle: rowTextBaseStyle,
        },
        includedCell: {
          // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
          textWrapperStyle: { height: '100%' },
          type: 'text',
          value: t(
            'report-local-government-fund-header-amount-equivalent-to-national-expense',
          ),
          textStyle: rowTextBaseStyle,
        },
      },
    },
    {
      type: 'multiple',
      ratio: 'auto',
      cell: {
        type: 'flex',
        vertical: true,
        cellRatios: ['1em', 1],
        flexCells: [
          {
            ...centerTextStyle,
            value: t(
              'report-local-government-fund-header-decision-on-project-implementation',
              { year: previousFiscalYear },
            ),
            textStyle: rowTextBaseStyle,
          },
          {
            type: 'multiple',
            cell: {
              type: 'flex',
              cellRatios: columnRatios.decisionOnProjectImplementation,
              flexCells: [
                `${t('fund-business-subsidy')}\n${t('report-local-government-fund-header-supplementary-for-local-government-fund-business')}`,
                t('investment'),
                t('loan'),
                t('debt-guarantee'),
                t('fund-business-for-local-government-others'),
              ].map((item) => ({
                type: 'multiple',
                cell: {
                  type: 'flex',
                  vertical: true,
                  borderStyle: 'dotted',
                  flexCells: [
                    {
                      ...centerTextStyle,
                      value: item,
                      textStyle: rowTextBaseStyle,
                    },
                    {
                      ...centerTextStyle,
                      value: `（${t('count')}）\n${t('amount')}`,
                      textStyle: rowTextBaseStyle,
                    },
                  ],
                },
              })),
            },
          },
        ],
      },
    },
    {
      type: 'multiple',
      ratio: 'auto',
      cell: {
        type: 'flex',
        vertical: true,
        cellRatios: ['1em', 1],
        flexCells: [
          {
            ...centerTextStyle,
            value: t('report-local-government-fund-header-loan-balance', {
              year: previousFiscalYear,
            }),
            textStyle: rowTextBaseStyle,
          },
          {
            type: 'multiple',
            cell: {
              type: 'flex',
              cellRatios: columnRatios.loanBalance,
              flexCells: [t('investment'), t('loan'), t('debt-guarantee')].map(
                (item) => ({
                  type: 'multiple',
                  cell: {
                    type: 'flex',
                    vertical: true,
                    borderStyle: 'dotted',
                    flexCells: [
                      {
                        ...centerTextStyle,
                        value: item,
                        textStyle: rowTextBaseStyle,
                      },
                      {
                        ...centerTextStyle,
                        value: `（${t('count')}）\n${t('amount')}`,
                        textStyle: rowTextBaseStyle,
                      },
                    ],
                  },
                }),
              ),
            },
          },
        ],
      },
    },
  ];
};

const incomeAndExpenditureCellRatios = [
  AMOUNT_CELL_WIDTH,
  `${AMOUNT_CELL_RATIO + 1}px`,
  `${AMOUNT_CELL_RATIO + 1}px`,
  AMOUNT_CELL_WIDTH,
  AMOUNT_CELL_WIDTH,
  `${AMOUNT_CELL_RATIO - 2}px`,
];

const getFundOwnerForLocalGovernmentRow = (
  data: z.infer<
    typeof ReportLocalGovernmentFundsTableFundOwnerForLocalGovernmentModel
  >['data'][number],
  isDebugMode: boolean,
): ReportTableCell[] => {
  const { result: isNullBeginningOfTheFundBalance } = validateTotalWithElements(
    {
      totalValue: data.endingOfTheFundBalance,
      elements: [
        {
          value: data.beginningOfTheFundBalance,
          rawValue: data.beginningOfTheFundBalance,
          negativeCount: 0,
        },
        {
          value: data.incomeAmount,
          rawValue: data.incomeAmount,
          negativeCount: 0,
        },
        {
          value: convertToNegative(data.businessExpense),
          rawValue: convertToNegative(data.businessExpense),
          negativeCount: 0,
        },
        {
          value: convertToNegative(data.amountReturnedToTheNationalTreasury),
          rawValue: convertToNegative(data.amountReturnedToTheNationalTreasury),
          negativeCount: 0,
        },
      ],
    },
  );

  return [
    {
      value: `${data.number}`,
      textStyle: rowTextBaseStyle,
    },
    {
      value: data.localFundOwner,
      textStyle: textLeftStyle,
    },
    {
      value: data.localFundName,
      textStyle: textLeftStyle,
    },
    {
      value: data.localFundOverview,
      textStyle: textLeftStyle,
    },
    //前々年度末基金残高
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        flexCells: [
          {
            value: getAmountText(data.beginningOfTheFundBalance),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          {
            value: getAmountText(
              data.nationalEquivalentOfBeginningOfTheFundBalance,
            ),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
        ],
      },
    },
    // 前年度収入・支出
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        cellRatios: incomeAndExpenditureCellRatios,
        flexCells: [
          // 収入(b) - うち国庫補助金額
          {
            value: getAmountText(data.incomeAmount),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          // うち国庫補助金額 - 国からの資金交付額
          {
            value: getAmountText(data.nationalEquivalentOfIncomeAmount),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          // 当初
          {
            value: getAmountText(data.grantedAmountByTheCountryFromInitial),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          // 補正
          {
            value: getAmountText(
              data.grantedAmountByTheCountryFromSupplementary,
            ),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          // 予備費等
          {
            value: getAmountText(data.grantedAmountByTheCountryFromReserve),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          // その他
          {
            value: getAmountText(data.otherIncomeAmount),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          // 支出(c)
          {
            value: getAmountText(data.businessExpense),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
        ],
      },
    },
    {
      value: getAmountText(data.amountReturnedToTheNationalTreasury),
      textStyle: amountTextStyle,
    },
    // 前年度末基金残高
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        flexCells: [
          {
            value: getAmountText(data.endingOfTheFundBalance, {
              isDebugMode,
              negativeCount:
                isNullBeginningOfTheFundBalance === 'isNull' ? 1 : 0,
            }),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          {
            value: getAmountText(
              data.nationalEquivalentOfTheEndOfTheFundBalance,
            ),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
        ],
      },
    },

    // 前年度の事業実施決定
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        flexCells: [
          {
            value: getCountAndAmountText({
              count: data.decidedNumberOfSubsidy,
              amount: data.decidedAmountOfSubsidy,
            }),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          {
            value: getCountAndAmountText({
              count: data.decidedNumberOfInvestment,
              amount: data.decidedAmountOfInvestment,
            }),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          {
            value: getCountAndAmountText({
              count: data.decidedNumberOfLoan,
              amount: data.decidedAmountOfLoan,
            }),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          {
            value: getCountAndAmountText({
              count: data.decidedNumberOfDebtGuarantee,
              amount: data.decidedAmountOfDebtGuarantee,
            }),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          {
            value: getCountAndAmountText({
              count: data.decidedNumberOfOthers,
              amount: data.decidedAmountOfOthers,
            }),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
        ],
      },
    },

    // 前年度貸付残高等
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        flexCells: [
          {
            value: getCountAndAmountText({
              count: data.balanceNumberOfInvestment,
              amount: data.balanceAmountOfInvestment,
            }),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          {
            value: getCountAndAmountText({
              count: data.balanceNumberOfLoan,
              amount: data.balanceAmountOfLoan,
            }),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
          {
            value: getCountAndAmountText({
              count: data.balanceNumberOfDebtGuarantee,
              amount: data.balanceAmountOfDebtGuarantee,
            }),
            textStyle: amountTextStyle,
            ...centerTextStyle,
          },
        ],
      },
    },
  ];
};

export const getFundOwnerForLocalGovernmentFooter = (
  summary:
    | z.infer<
        typeof ReportLocalGovernmentFundsTableFundOwnerForLocalGovernmentModel
      >['summaryData']
    | undefined,
  isDebugMode: boolean,
): ReportTableProps['footer'] => {
  const { result: isNullBeginningOfTheFundBalance } = validateTotalWithElements(
    {
      totalValue: summary?.endingOfTheFundBalance,
      elements: [
        {
          value: summary?.beginningOfTheFundBalance,
          rawValue: summary?.beginningOfTheFundBalance,
          negativeCount: summary?.negativeBeginningOfTheFundBalanceCount,
        },
        {
          value: summary?.incomeAmount,
          rawValue: summary?.incomeAmount,
          negativeCount: summary?.negativeIncomeAmountCount,
        },
        {
          value: convertToNegative(summary?.businessExpense),
          rawValue: convertToNegative(summary?.businessExpense),
          negativeCount: summary?.negativeBusinessExpenseCount,
        },
        {
          value: convertToNegative(
            summary?.amountReturnedToTheNationalTreasury,
          ),
          rawValue: convertToNegative(
            summary?.amountReturnedToTheNationalTreasury,
          ),
          negativeCount:
            summary?.negativeAmountReturnedToTheNationalTreasuryCount,
        },
      ],
    },
  );

  return [
    {
      row: [
        {
          value: t(
            'report-fund-owner-for-local-government-fund-owner-total-amount',
          ),
          textStyle: rowTextBaseStyle,
        },
        {
          value: summary?.numberOfLocalFundOwner || FALLBACK_TEXT,
          textStyle: rowTextBaseStyle,
        },
        { value: '' },
        { value: '' },

        //前々年度末基金残高
        {
          type: 'multiple',
          cell: {
            type: 'flex',
            flexCells: [
              {
                value: getAmountText(summary?.beginningOfTheFundBalance, {
                  isDebugMode,
                  negativeCount:
                    summary?.negativeBeginningOfTheFundBalanceCount,
                }),
                textStyle: amountTextStyle,
                ...centerTextStyle,
              },
              {
                value: getAmountText(
                  summary?.nationalEquivalentOfBeginningOfTheFundBalance,
                  {
                    isDebugMode,
                    negativeCount:
                      summary?.negativeNationalEquivalentOfBeginningOfTheFundBalanceCount,
                  },
                ),
                textStyle: amountTextStyle,
                ...centerTextStyle,
              },
            ],
          },
        },
        // 前年度収入・支出
        {
          type: 'multiple',
          cell: {
            cellRatios: incomeAndExpenditureCellRatios,
            flexCells: [
              {
                // 収入(b) - うち国庫補助金額
                value: getAmountText(summary?.incomeAmount, {
                  isDebugMode,
                  negativeCount: summary?.negativeIncomeAmountCount,
                }),
                textStyle: amountTextStyle,
                ...centerTextStyle,
              },
              {
                // うち国庫補助金額 - 国からの資金交付額
                value: getAmountText(
                  summary?.nationalEquivalentOfIncomeAmount,
                  {
                    isDebugMode,
                    negativeCount:
                      summary?.negativeNationalEquivalentOfIncomeAmountCount,
                  },
                ),
                textStyle: amountTextStyle,
                ...centerTextStyle,
              },
              {
                // 当初
                value: getAmountText(
                  summary?.grantedAmountByTheCountryFromInitial,
                  {
                    isDebugMode,
                    negativeCount:
                      summary?.negativeGrantedAmountByTheCountryFromInitialCount,
                  },
                ),
                textStyle: amountTextStyle,
                ...centerTextStyle,
              },
              {
                // 補正
                value: getAmountText(
                  summary?.grantedAmountByTheCountryFromSupplementary,
                  {
                    isDebugMode,
                    negativeCount:
                      summary?.negativeGrantedAmountByTheCountryFromSupplementaryCount,
                  },
                ),
                textStyle: amountTextStyle,
                ...centerTextStyle,
              },
              {
                // 予備費等
                value: getAmountText(
                  summary?.grantedAmountByTheCountryFromReserve,
                  {
                    isDebugMode,
                    negativeCount:
                      summary?.negativeGrantedAmountByTheCountryFromReserveCount,
                  },
                ),
                textStyle: amountTextStyle,
                ...centerTextStyle,
              },
              {
                // その他
                value: getAmountText(summary?.otherIncomeAmount, {
                  isDebugMode,
                  negativeCount: summary?.negativeOtherIncomeAmountCount,
                }),
                textStyle: amountTextStyle,
                ...centerTextStyle,
              },
              {
                // 支出(c)
                value: getAmountText(summary?.businessExpense, {
                  isDebugMode,
                  negativeCount: summary?.negativeBusinessExpenseCount,
                }),
                textStyle: amountTextStyle,
                ...centerTextStyle,
              },
            ],
          },
        },
        {
          type: 'text',
          value: getAmountText(summary?.amountReturnedToTheNationalTreasury, {
            isDebugMode,
            negativeCount:
              summary?.negativeAmountReturnedToTheNationalTreasuryCount,
          }),
          textStyle: amountTextStyle,
        },

        // 前年度末基金残高
        {
          type: 'multiple',
          cell: {
            type: 'flex',
            flexCells: [
              {
                value: getAmountText(summary?.endingOfTheFundBalance, {
                  isDebugMode,
                  negativeCount:
                    isNullBeginningOfTheFundBalance === 'isNull' ||
                    summary?.negativeEndingOfTheFundBalanceCount
                      ? 1
                      : 0,
                }),
                textStyle: amountTextStyle,
                ...centerTextStyle,
              },
              {
                value: getAmountText(
                  summary?.nationalEquivalentOfTheEndOfTheFundBalance,
                  {
                    isDebugMode,
                    negativeCount:
                      summary?.negativeNationalEquivalentOfTheEndOfTheFundBalanceCount,
                  },
                ),
                textStyle: amountTextStyle,
                ...centerTextStyle,
              },
            ],
          },
        },

        // 前年度の事業実施決定
        {
          type: 'multiple',
          cell: {
            type: 'flex',
            flexCells: [
              {
                type: 'text',
                value: getCountAndAmountText({
                  count: summary?.decidedNumberOfSubsidy,
                  amount: summary?.decidedAmountOfSubsidy,
                  negativeCount: summary?.negativeDecidedAmountOfSubsidyCount,
                  isDebugMode,
                }),
                textStyle: amountTextStyle,
              },
              {
                type: 'text',
                value: getCountAndAmountText({
                  count: summary?.decidedNumberOfInvestment,
                  amount: summary?.decidedAmountOfInvestment,
                  negativeCount:
                    summary?.negativeDecidedAmountOfInvestmentCount,
                  isDebugMode,
                }),
                textStyle: amountTextStyle,
              },
              {
                type: 'text',
                value: getCountAndAmountText({
                  count: summary?.decidedNumberOfLoan,
                  amount: summary?.decidedAmountOfLoan,
                  negativeCount: summary?.negativeDecidedAmountOfLoanCount,
                  isDebugMode,
                }),
                textStyle: amountTextStyle,
              },
              {
                type: 'text',
                value: getCountAndAmountText({
                  count: summary?.decidedNumberOfDebtGuarantee,
                  amount: summary?.decidedAmountOfDebtGuarantee,
                  negativeCount:
                    summary?.negativeDecidedAmountOfDebtGuaranteeCount,
                  isDebugMode,
                }),
                textStyle: amountTextStyle,
              },
              {
                type: 'text',
                value: getCountAndAmountText({
                  count: summary?.decidedNumberOfOthers,
                  amount: summary?.decidedAmountOfOthers,
                  negativeCount: summary?.negativeDecidedAmountOfOthersCount,
                  isDebugMode,
                }),
                textStyle: amountTextStyle,
              },
            ],
          },
        },

        // 前年度貸付残高等
        {
          type: 'multiple',
          cell: {
            type: 'flex',
            flexCells: [
              {
                type: 'text',
                value: getCountAndAmountText({
                  count: summary?.balanceNumberOfInvestment,
                  amount: summary?.balanceAmountOfInvestment,
                  negativeCount:
                    summary?.negativeBalanceAmountOfInvestmentCount,
                  isDebugMode,
                }),
                textStyle: amountTextStyle,
              },
              {
                type: 'text',
                value: getCountAndAmountText({
                  count: summary?.balanceNumberOfLoan,
                  amount: summary?.balanceAmountOfLoan,
                  negativeCount: summary?.negativeBalanceAmountOfLoanCount,
                  isDebugMode,
                }),
                textStyle: amountTextStyle,
              },
              {
                type: 'text',
                value: getCountAndAmountText({
                  count: summary?.balanceNumberOfDebtGuarantee,
                  amount: summary?.balanceAmountOfDebtGuarantee,
                  negativeCount:
                    summary?.negativeBalanceAmountOfDebtGuaranteeCount,
                  isDebugMode,
                }),
                textStyle: amountTextStyle,
              },
            ],
          },
        },
      ],
    },
  ];
};

export const getFundOwnerForLocalGovernmentData = ({
  fiscalYear,
  data,
  isDebugMode,
}: {
  fiscalYear: number;
  data: z.infer<
    typeof ReportLocalGovernmentFundsTableFundOwnerForLocalGovernmentModel
  >;
  isDebugMode: boolean;
}) => {
  return {
    header: getFundOwnerForLocalGovernmentHeader(fiscalYear),
    data: data.data.map<ReportTableProps['data'][number]>((d) => ({
      row: getFundOwnerForLocalGovernmentRow(d, isDebugMode),
      rowStyle: {
        breakInside: 'avoid',
      },
    })),
    footer: getFundOwnerForLocalGovernmentFooter(data.summaryData, isDebugMode),
  };
};
